import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Divider,
  InputAdornment,
  CircularProgress,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  CreatePaymentMethod,
  UpdatePaymentMethod,
  UploadPicture,
} from "../../redux/slices/payment";

const initialForm = {
  type: "",
  accountName: "",
  accountValue: "",
  depositLink: "",
  withdrawLink: "",
  isActive: true,
  icon: "",
};

const AddPaymentMethodModal = ({
  open,
  onClose,
  onCreated,
  method,
  onUpdated,
}) => {
  const [form, setForm] = useState(initialForm);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();

  // Mock uploadImage function (replace this with real upload API)
  async function uploadImage(file) {
    setUploading(true);
    const image = await dispatch(UploadPicture(file));
    if (image) {
      setForm((s) => ({ ...s, icon: image }));
      setPreview(image);
      toast.success("Icon uploaded successfully!");
    }
    setUploading(false);
  }

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    uploadImage(file);
  }

  function handleRemoveIcon() {
    setForm((s) => ({ ...s, icon: "" }));
    setPreview(null);
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  }

  function handleToggle(e) {
    const { checked } = e.target;
    setForm((s) => ({ ...s, isActive: checked }));
  }

  useEffect(() => {
    if (method) {
      setForm({
        type: method.type || "",
        accountName: method.accountName || "",
        accountValue: method.accountValue || "",
        depositLink: method.depositLink || "",
        withdrawLink: method.withdrawLink || "",
        isActive: typeof method.isActive === "boolean" ? method.isActive : true,
        icon: method.icon || "",
      });
      setPreview(method.icon || null);
    } else if (!open) {
      setForm(initialForm);
      setPreview(null);
    }
  }, [method, open]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.type) return toast.error("Type is required");

    setSubmitting(true);
    if (method && method._id) {
      const updated = await dispatch(UpdatePaymentMethod(method._id, form));
      if (updated) {
        toast.success("Payment method updated");
        onUpdated && onUpdated(updated);
        onClose();
      }
    } else {
      const created = await dispatch(CreatePaymentMethod(form));
      if (created) {
        toast.success("Payment method created");
        setForm(initialForm);
        setPreview(null);
        onCreated && onCreated(created);
        onClose();
      }
    }
    setSubmitting(false);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        <Typography variant="h6">
          {method ? "Edit" : "Add"} Payment Method
        </Typography>
      </DialogTitle>

      <Divider />

      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent dividers>
          <div className="space-y-4">
            {/* Upload Icon */}
            <Box
              sx={{
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                textAlign: "center",
                position: "relative",
                cursor: uploading ? "not-allowed" : "pointer",
                "&:hover": { borderColor: "primary.main" },
              }}
              onClick={() =>
                !uploading && document.getElementById("icon-upload").click()
              }
            >
              {uploading ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 72,
                  }}
                >
                  <CircularProgress size={28} />
                </Box>
              ) : preview ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  <Avatar
                    src={preview}
                    alt="icon"
                    sx={{ width: 72, height: 72, borderRadius: 2 }}
                  />
                  <Tooltip title="Remove Icon">
                    <IconButton
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: "calc(50% - 36px)",
                        transform: "translateX(50%)",
                        backgroundColor: "background.paper",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveIcon();
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <Box>
                  <UploadFileIcon color="action" sx={{ fontSize: 40 }} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Click to upload payment method icon
                  </Typography>
                </Box>
              )}
              <input
                type="file"
                accept="image/*"
                id="icon-upload"
                hidden
                onChange={handleFileChange}
              />
            </Box>

            <TextField
              label="Type"
              name="type"
              value={form.type}
              onChange={handleChange}
              required
              fullWidth
              helperText="Select the payment method type"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextField
                label="Account Name"
                name="accountName"
                value={form.accountName}
                onChange={handleChange}
                fullWidth
                placeholder="e.g., Zelle name, Chime display name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">🏦</InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Account Value"
                name="accountValue"
                value={form.accountValue}
                onChange={handleChange}
                fullWidth
                placeholder="e.g., email@example.com, Zelle/Chime tag, account number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">🔢</InputAdornment>
                  ),
                }}
              />
            </div>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              For card payment method
            </Typography>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextField
                label="Deposit Link"
                name="depositLink"
                value={form.depositLink}
                onChange={handleChange}
                fullWidth
                placeholder="https://..."
                helperText="(Optional) URL used for deposits"
              />

              <TextField
                label="Withdraw Link"
                name="withdrawLink"
                value={form.withdrawLink}
                onChange={handleChange}
                fullWidth
                placeholder="https://..."
                helperText="(Optional) URL used for withdrawals"
              />
            </div>

            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={handleToggle}
                  color="primary"
                />
              }
              label="Active"
            />
          </div>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} /> : null}
          >
            {submitting ? "Saving" : method ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default AddPaymentMethodModal;
